import { useEffect, useMemo, useState } from "react";

import { useUpdateFacilityUserAlertSettings } from "./api/useUpdateFacilityUserAlertSettings";
import { HOME_HEALTH_ALERT_ROWS, MASTER_ALERT_KEY } from "./constants";
import { HomeHealthNotificationPreferencesView } from "./HomeHealthNotificationPreferencesView";
import { type HomeHealthAlertEnabledMap, type HomeHealthUser } from "./types";
import { buildMergedAlertSettings, getEmailRowEnabled } from "./utils/buildMergedAlertSettings";

interface HomeHealthNotificationPreferencesContainerProps {
  /**
   * The user whose preferences are being edited. Callers pass already-loaded
   * data (from useGetCurrentUser, the team-members list, or Redux) so this
   * container works for any signed-in role — workplace users cannot read
   * /facilityUser/{id} for themselves. Pass `undefined` while loading; the
   * Container will render its own spinner.
   */
  user: HomeHealthUser | undefined;
  workplaceId: string;
  performedBy?: string;
  onSaved?: () => void;
}

export function HomeHealthNotificationPreferencesContainer(
  props: HomeHealthNotificationPreferencesContainerProps
) {
  const { user, workplaceId, performedBy, onSaved } = props;

  const updateAlertSettings = useUpdateFacilityUserAlertSettings();

  const [savingKey, setSavingKey] = useState<string | undefined>(undefined);

  // Overlay so toggles don't visually revert before the parent's data source
  // (e.g. the team-members list's cached member snapshot) catches up.
  const [optimisticEdits, setOptimisticEdits] = useState<HomeHealthAlertEnabledMap>({});

  // Drop optimistic edits that the parent has caught up on. Return the same
  // reference when nothing changed so React skips the re-render.
  useEffect(() => {
    const persisted = user?.alertSettings;
    if (!persisted) {
      return;
    }
    setOptimisticEdits((current) => {
      const next: HomeHealthAlertEnabledMap = {};
      for (const [alertKey, enabled] of Object.entries(current)) {
        if (getEmailRowEnabled(persisted, alertKey) !== enabled) {
          next[alertKey] = enabled;
        }
      }
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [user?.alertSettings]);

  const masterEnabled =
    optimisticEdits[MASTER_ALERT_KEY] ?? getEmailRowEnabled(user?.alertSettings, MASTER_ALERT_KEY);

  const enabledByKey = useMemo<HomeHealthAlertEnabledMap>(
    () =>
      Object.fromEntries(
        HOME_HEALTH_ALERT_ROWS.map((row) => [
          row.alertKey,
          optimisticEdits[row.alertKey] ?? getEmailRowEnabled(user?.alertSettings, row.alertKey),
        ])
      ),
    [user?.alertSettings, optimisticEdits]
  );

  function submit(params: { savingKey: string; edits: HomeHealthAlertEnabledMap }) {
    if (!user || updateAlertSettings.isLoading) {
      return;
    }
    setSavingKey(params.savingKey);

    // Carry every pending optimistic edit so a save built on a stale parent
    // snapshot doesn't silently overwrite previously-saved keys.
    const cumulativeEdits: HomeHealthAlertEnabledMap = { ...optimisticEdits, ...params.edits };
    setOptimisticEdits(cumulativeEdits);

    const mergedAlertSettings = buildMergedAlertSettings({
      existing: user.alertSettings,
      edits: cumulativeEdits,
    });

    updateAlertSettings.mutate(
      {
        facilityId: workplaceId,
        firebaseId: user.firebaseId,
        performedBy,
        userId: user._id,
        userInfo: {
          alertSettings: mergedAlertSettings,
          notify: user.notify,
        },
      },
      {
        onSuccess: () => onSaved?.(),
        onError: () => {
          setOptimisticEdits((current) => {
            const next = { ...current };
            for (const alertKey of Object.keys(params.edits)) {
              delete next[alertKey];
            }
            return next;
          });
        },
        onSettled: () => setSavingKey(undefined),
      }
    );
  }

  const handleToggleRow = (alertKey: string, enabled: boolean) => {
    submit({ savingKey: alertKey, edits: { [alertKey]: enabled } });
  };

  // Writes EMAIL.All directly — the backend mirrors this to Knock's channel-
  // level mute, which suppresses every workflow on that channel for this user
  // across every workplace they belong to. A multi-workplace user editing
  // from one workplace's page therefore affects all of them.
  const handleToggleAll = (enabled: boolean) => {
    submit({ savingKey: MASTER_ALERT_KEY, edits: { [MASTER_ALERT_KEY]: enabled } });
  };

  return (
    <HomeHealthNotificationPreferencesView
      enabledByKey={enabledByKey}
      isLoading={!user}
      masterEnabled={masterEnabled}
      savingKey={savingKey}
      onToggleAll={handleToggleAll}
      onToggleRow={handleToggleRow}
    />
  );
}
